import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Link2, Sparkles, Loader2, Mail } from 'lucide-react';
import { sellerApi, setSession } from '../sellerAuth';

const slides = [
  { icon: Zap, title: 'Get paid in seconds', text: 'Accept card payments and watch the money land in your balance instantly.' },
  { icon: Link2, title: 'Sell from anywhere', text: 'Charge a card in person or share a link — no extra hardware needed.' },
  { icon: Sparkles, title: 'Grow with FLIX', text: 'Track sales, manage payouts and run your business from your phone.' },
];

const SellerAuth = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..2 welcome, 3 auth
  const [mode, setMode] = useState('signup');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await sellerApi.post('/seller/forgot-password', { email: form.email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (mode === 'signup') {
        const res = await sellerApi.post('/seller/signup', form);
        setSession(res.data.token, res.data.seller);
        navigate('/activate');
      } else {
        const res = await sellerApi.post('/seller/login', { email: form.email, password: form.password });
        setSession(res.data.token, res.data.seller);
        navigate('/activate');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  if (step < 3) {
    const S = slides[step];
    const Icon = S.icon;
    return (
      <div className="min-h-screen bg-[#0b1510] text-white flex flex-col px-7 py-10 max-w-md mx-auto">
        <div className="flex justify-between items-center">
          <span className="brand text-2xl">flix<span className="text-[#3ddc9a]">.</span></span>
          <button onClick={() => setStep(3)} className="text-white/50 text-sm font-semibold">Skip</button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-3xl bg-[#0e9f6e]/15 flex items-center justify-center mb-8 floaty">
            <Icon size={44} className="text-[#3ddc9a]" />
          </div>
          <h1 className="font-display text-4xl font-700 leading-tight px-2">{S.title}</h1>
          <p className="text-white/55 mt-4 max-w-xs">{S.text}</p>
        </div>
        <div className="flex justify-center gap-2 mb-8">
          {slides.map((_, i) => (
            <span key={i} className={`h-2 rounded-full transition-all ${i === step ? 'w-8 bg-[#3ddc9a]' : 'w-2 bg-white/20'}`} />
          ))}
        </div>
        <button data-testid="onboarding-next-btn" onClick={() => setStep(step + 1 >= 3 ? 3 : step + 1)} className="w-full bg-[#0e9f6e] text-white rounded-2xl py-4 text-base font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          {step === 2 ? 'Get started' : 'Next'} <ArrowRight size={18} />
        </button>
      </div>
    );
  }

  if (forgot) {
    return (
      <div className="min-h-screen bg-[#0b1510] text-white flex flex-col px-7 py-10 max-w-md mx-auto">
        <span className="brand text-2xl mb-8">flix<span className="text-[#3ddc9a]">.</span></span>
        {sent ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-20 h-20 rounded-3xl bg-[#0e9f6e]/15 flex items-center justify-center mb-6"><Mail size={40} className="text-[#3ddc9a]" /></div>
            <h1 className="font-display text-3xl font-700">Check your email</h1>
            <p className="text-white/55 mt-3 max-w-xs">If an account exists for <b className="text-white">{form.email}</b>, we've sent a link to reset your password. It expires in 1 hour.</p>
            <button data-testid="forgot-back-btn" onClick={() => { setForgot(false); setSent(false); }} className="mt-8 text-sm text-[#3ddc9a] font-semibold">Back to log in</button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-700">Reset password</h1>
            <p className="text-white/55 mt-2 mb-7 text-sm">Enter your account email and we'll send you a reset link.</p>
            <form onSubmit={submitForgot} className="space-y-3">
              <input data-testid="forgot-email-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required placeholder="Email"
                className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
              {error && <p className="text-sm text-[#ff6b5e]">{error}</p>}
              <button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full bg-[#0e9f6e] text-white rounded-2xl py-4 text-base font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <>Send reset link <ArrowRight size={18} /></>}
              </button>
            </form>
            <button onClick={() => { setForgot(false); setError(''); }} className="mt-6 text-sm text-white/60 text-center">Back to log in</button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1510] text-white flex flex-col px-7 py-10 max-w-md mx-auto">
      <span className="brand text-2xl mb-8">flix<span className="text-[#3ddc9a]">.</span></span>
      <h1 className="font-display text-3xl font-700">{mode === 'signup' ? 'Create your account' : 'Welcome back'}</h1>
      <p className="text-white/55 mt-2 mb-7 text-sm">{mode === 'signup' ? 'Start accepting payments in minutes.' : 'Log in to your FLIX seller account.'}</p>

      <form onSubmit={submit} className="space-y-3">
        {mode === 'signup' && (
          <input data-testid="auth-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} required placeholder="Name or business"
            className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
        )}
        <input data-testid="auth-email-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required placeholder="Email"
          className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
        {mode === 'signup' && (
          <input data-testid="auth-phone-input" type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} required placeholder="Phone number"
            className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
        )}
        <input data-testid="auth-password-input" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} required minLength={6} placeholder="Password"
          className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none focus:border-[#3ddc9a] transition" />
        {error && <p className="text-sm text-[#ff6b5e]">{error}</p>}
        <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full bg-[#0e9f6e] text-white rounded-2xl py-4 text-base font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <>{mode === 'signup' ? 'Create account' : 'Log in'} <ArrowRight size={18} /></>}
        </button>
      </form>

      {mode === 'login' && (
        <button data-testid="forgot-open-btn" onClick={() => { setForgot(true); setError(''); }} className="mt-4 text-sm text-white/60 text-center">Forgot password?</button>
      )}

      <button data-testid="auth-toggle-mode-btn" onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); setError(''); }} className="mt-6 text-sm text-white/60 text-center">
        {mode === 'signup' ? 'Already have an account? Log in' : "New to FLIX? Create an account"}
      </button>
    </div>
  );
};

export default SellerAuth;
